import { __rest } from "tslib";
import { embeddedBundle } from './locale/embeddedI18N';
export const versionInfo = { major: 2, minor: 4, patch: 1 };
const events = [
    'resize',
    'orientationchange',
    'mousemove',
    'mousedown',
    'mouseup',
    'touchstart',
    'touchmove',
    'touchend',
    'touchcancel',
    'keydown',
    'keyup',
    'wheel',
];
let keepAliveInterval = 15 * 60000;
let keepAliveIntervalId = 0;
function keepAlive() {
    if (!isActive) {
        clearInterval(keepAliveIntervalId);
        keepAliveIntervalId = 0;
        return;
    }
    isActive = false;
    for (const [host, item] of authMap) {
        if (host.startsWith('guest-token:') || item.status !== 'authorized') {
            continue;
        }
        fetch(`${host}/SASLogon/`, {
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).catch(() => { });
    }
}
let isActive = false;
function setActive(forceKeepalive = false) {
    isActive = true;
    // If we don't have an interval running, then we've run a full cycle without firing a keepalive
    //  Since our interval has elapsed, immediately fire off keepalive requests
    if (!keepAliveIntervalId || forceKeepalive === true) {
        keepAlive();
        window.clearInterval(keepAliveIntervalId);
        keepAliveIntervalId = window.setInterval(keepAlive, keepAliveInterval);
    }
}
let statusCbs = new Map();
let authMap = new Map();
let userIdMap = new Map();
let windowFocusCb;
function getKey(server, auth) {
    if (auth === 'guest-token') {
        return `${auth}:${server}`;
    }
    return server;
}
export function getAuthUrl(server) {
    return `${server}/SASVisualAnalytics/logon/index.html`;
}
export function setState(state) {
    statusCbs = new Map(state.statusCbs);
    authMap = new Map(state.authMap);
    userIdMap = new Map(state.userIdMap);
}
async function updateUserId(server) {
    const userResp = await fetch(`${server}/identities/users/@currentUser`, {
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
    });
    if (!userResp.ok) {
        throw new Error('[authorization] Unable to update user, invalid identities response');
    }
    try {
        const userJson = await userResp.json();
        userIdMap.set(server, userJson.id);
    }
    catch (e) {
        throw new Error('[authorization] Unable to update user, invalid identities json response');
    }
}
async function performGuestLogon(server) {
    const reportsResp = await fetch(`${server}/reports/?sso_guest=true`, {
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
    });
    if (!reportsResp.ok) {
        throw new Error('invalid logon response');
    }
}
async function getGuestToken(server) {
    var _a;
    const resp = await fetch(`${server}/SASLogon/oauth/token`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
            // eslint-disable-next-line @sas/i18n/jsx-to-localize
            Authorization: `Basic ${btoa('sas.mobilebi:')}`,
            Accept: 'application/json, */*',
        },
        body: new URLSearchParams('grant_type=guest'),
    });
    if (!resp.ok) {
        return [undefined, '[Authorization]: Failed to fetch guest token.'];
    }
    let token;
    try {
        token = ((_a = (await resp.json())) === null || _a === void 0 ? void 0 : _a.access_token) || undefined;
        // eslint-disable-next-line no-empty
    }
    catch (_b) { }
    if (!token) {
        return [undefined, '[Authorization]: no guest token in response.'];
    }
    return [token, undefined];
}
async function initGuestToken(server) {
    const [token, error] = await getGuestToken(server);
    if (error) {
        console.error(error);
        updateAuthMapValue(server, 'guest-token', {
            status: 'initalizing',
            error: embeddedBundle['SASServer.logonError.msg'](),
        });
        return;
    }
    updateAuthMapValue(server, 'guest-token', {
        status: 'authorized',
        token: token,
    });
}
function updateAuthMapValue(server, authenticationType, value) {
    const authInfo = authMap.get(getKey(server, authenticationType));
    if (!authInfo) {
        throw new Error('No auth found');
    }
    setAuthMapValue(server, authenticationType, Object.assign(Object.assign({}, authInfo), value));
}
function setAuthMapValue(server, authenticationType, value) {
    authMap.set(getKey(server, authenticationType), value);
    const { guestType, serverHack: reportsHack } = value, status = __rest(value, ["guestType", "serverHack"]);
    for (const cb of statusCbs.get(server) || []) {
        cb(status);
    }
}
const authInterfaceImpl = {
    versionInfo,
    getKey,
    onStatusChange(server, cb, authenticationType) {
        const cbs = statusCbs.get(getKey(server, authenticationType)) || [];
        cbs.push(cb);
        statusCbs.set(server, cbs);
    },
    getValue(server, tokenType) {
        return authMap.get(getKey(server, tokenType));
    },
    async _logonAsGuest(server) {
        try {
            await performGuestLogon(server);
        }
        catch (e) {
            updateAuthMapValue(server, 'guest', {
                status: 'initalizing',
                error: embeddedBundle['SASServer.logonError.msg'](),
            });
            throw new Error('[authorization] Unable to log in as guest.');
        }
        this.authorize(server, 'guest');
    },
    addConnection(server, authenticationType) {
        let authInfo = authMap.get(getKey(server, authenticationType));
        if (authenticationType === 'guest-token') {
            if (authInfo) {
                return authInfo;
            }
            authInfo = {
                status: 'initalizing',
                guestType: 'token',
            };
            setAuthMapValue(server, authenticationType, authInfo);
            initGuestToken(server);
            return authInfo;
        }
        if (authInfo) {
            if (authenticationType === 'guest') {
                if (authInfo.status === 'credentials-invalid') {
                    updateAuthMapValue(server, 'guest', {
                        status: 'initalizing',
                        guestType: 'credential',
                    });
                    this._logonAsGuest(server);
                }
                else {
                    updateAuthMapValue(server, 'guest', {
                        guestType: 'credential',
                    });
                }
            }
            return authInfo;
        }
        authInfo = {
            status: 'initalizing',
            guestType: authenticationType === 'guest' ? 'credential' : undefined,
        };
        setAuthMapValue(server, authenticationType, authInfo);
        (async () => {
            var _a, _b;
            try {
                const resp = await fetch(getAuthUrl(server), { credentials: 'include' });
                const text = await resp.text();
                if (text.includes('data-report-sas-auth-success')) {
                    updateAuthMapValue(server, 'credentials', {
                        status: 'authorized',
                    });
                    updateUserId(server);
                }
                else {
                    if (((_a = authMap.get(getKey(server, authenticationType))) === null || _a === void 0 ? void 0 : _a.guestType) === 'credential') {
                        await this._logonAsGuest(server);
                    }
                    else {
                        updateAuthMapValue(server, 'credentials', {
                            status: 'credentials-invalid',
                        });
                    }
                }
            }
            catch (error) {
                if (((_b = authMap.get(getKey(server, authenticationType))) === null || _b === void 0 ? void 0 : _b.guestType) === 'credential') {
                    // This may occur with custom SSO, try and make the sso_guest login since this bypasses
                    //   custom SSO
                    await this._logonAsGuest(server);
                }
                else {
                    console.warn('[Authorization]: unable to determine auth status', error);
                    updateAuthMapValue(server, 'credentials', {
                        status: 'credentials-invalid',
                    });
                }
            }
        })();
        return authInfo;
    },
    activate() {
        var _a, _b;
        if (windowFocusCb) {
            return;
        }
        windowFocusCb = async () => {
            setActive(true);
            for (const [host, item] of authMap) {
                if (item.status !== 'authorized') {
                    continue;
                }
                const reportsResp = await fetch(`${host}/reports/`, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'include',
                });
                if (!reportsResp.ok || reportsResp.url.includes('/SASLogon/')) {
                    authInterfaceImpl.deauthorize(host);
                }
            }
        };
        // @ts-ignore
        if ((_b = (_a = window.sas) === null || _a === void 0 ? void 0 : _a.ltjs) === null || _b === void 0 ? void 0 : _b.jest) {
            return;
        }
        if (!keepAliveIntervalId) {
            keepAliveIntervalId = window.setInterval(keepAlive, keepAliveInterval);
            for (const event of events) {
                window.addEventListener(event, setActive, { capture: true });
            }
        }
        window.addEventListener('focus', windowFocusCb);
    },
    deactivate() {
        if (keepAliveIntervalId) {
            window.clearInterval(keepAliveIntervalId);
            keepAliveIntervalId = 0;
        }
        for (const event of events) {
            window.removeEventListener(event, setActive, { capture: true });
        }
        if (windowFocusCb) {
            window.removeEventListener('focus', windowFocusCb);
            windowFocusCb = undefined;
        }
        statusCbs.clear();
        authMap.clear();
        userIdMap.clear();
    },
    deauthorize(server, authenticationType) {
        const authInfo = authMap.get(getKey(server, authenticationType));
        if (!authInfo || authInfo.status !== 'authorized') {
            return;
        }
        if (authInfo.guestType === 'token') {
            updateAuthMapValue(server, 'guest-token', {
                status: 'initalizing',
                token: undefined,
            });
            initGuestToken(server);
            return;
        }
        if (!authInfo.guestType) {
            updateAuthMapValue(server, authenticationType, {
                status: 'credentials-invalid',
                serverHack: undefined,
            });
            return;
        }
        updateAuthMapValue(server, authenticationType, {
            status: 'initalizing',
            serverHack: undefined,
        });
        // timeout because we can can get a deauthorize call per 401 request result.
        setTimeout(async () => {
            const [token] = await getGuestToken(server);
            if (!token) {
                updateAuthMapValue(server, authenticationType, {
                    status: 'credentials-invalid',
                });
                throw new Error('[authorization] unable to re-authorize guest.');
            }
            const userResp = await fetch(`${server}/identities/users/@currentUser`, {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    // eslint-disable-next-line @sas/i18n/jsx-to-localize
                    Authorization: `bearer ${token}`,
                },
                credentials: 'omit',
            });
            if (!userResp.ok) {
                updateAuthMapValue(server, authenticationType, {
                    status: 'credentials-invalid',
                });
                throw new Error('[authorization] unable to re-authorize guest.');
            }
            try {
                const json = await userResp.json();
                if (json.id !== userIdMap.get(server)) {
                    throw new Error('[authorization] user is not guest.');
                }
                // We are guest!
                await this._logonAsGuest(server);
            }
            catch (e) {
                updateAuthMapValue(server, authenticationType, {
                    status: 'credentials-invalid',
                });
                throw new Error('[authorization] unable to re-authorize guest.');
            }
        }, 15);
    },
    authorize(server, authenticationType) {
        const info = authMap.get(getKey(server, authenticationType));
        if ((info === null || info === void 0 ? void 0 : info.status) === 'authorized' || (info === null || info === void 0 ? void 0 : info.serverHack)) {
            return;
        }
        if (authenticationType === 'guest-token') {
            updateAuthMapValue(server, authenticationType, {
                status: 'authorized',
            });
            return;
        }
        updateAuthMapValue(server, authenticationType, {
            serverHack: true,
            status: 'initalizing',
        });
        // Making multiple requests to an endpoint before it has gone through the authorization loop
        //  can cause problems due to a limitation in the Viya platform. To work around this, make the
        //  /reports/ and /identities/ requests before we are fully authorized.
        Promise.all([
            updateUserId(server),
            fetch(`${server}/reports/`, {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'include',
            }).catch(() => { }),
        ]).then(() => {
            updateAuthMapValue(server, authenticationType, {
                status: 'authorized',
                serverHack: undefined,
            });
        });
    },
    _removeServer(server, authenticationType) {
        if (authenticationType) {
            authMap.delete(getKey(server, authenticationType));
        }
        else {
            authMap.delete(server);
            userIdMap.delete(server);
        }
    },
    _getState() {
        return {
            statusCbs,
            authMap,
            userIdMap,
        };
    },
    setKeepAliveInterval(minutes) {
        if (isNaN(minutes) || typeof minutes !== 'number') {
            keepAliveInterval = 60000;
        }
        else {
            keepAliveInterval = Math.min(minutes, 1) * 60000;
        }
        if (keepAliveIntervalId) {
            window.clearInterval(keepAliveIntervalId);
            keepAliveIntervalId = window.setInterval(keepAlive, keepAliveInterval);
        }
    },
};
export const authInterface = authInterfaceImpl;
//# sourceMappingURL=authorizeImpl.js.map